import { LDate } from '@app/components/date/LDate';
import { LTime } from '@app/components/date/LTime';
import { YoutubeBroadcast, YoutubeBroadcastVideo } from '../../youtube-broadcast-types';
import { DurationForRange } from '@mi-front/ui';
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { YoutubeBroadcastPeakViewers } from '../shared/YoutubeBroadcastPeakViewers';
import { YoutubeBroadcastThumbnail } from '../shared/YoutubeBroadcastThumbnail';
import { YOUTUBE_BROADCAST_THUMBNAIL_WIDTH } from '../shared/consts';

type YoutubeBroadcastItemLayout = 'horizontal' | 'vertical';
export type YoutubeBroadcastItemSize = 'xsmall' | 'small' | 'medium' | 'large';

interface YoutubeBroadcastItemProps {
  broadcast: YoutubeBroadcast;
  stationSlug: string;
  /** Vertical: list row. Horizontal: status screen panorama strip. Both show the accent ribbon. */
  layout?: YoutubeBroadcastItemLayout;
  /** Visual density. Large is the default list style; medium tightens horizontal title clamping. */
  size?: YoutubeBroadcastItemSize;
  /** Fixed item height for horizontal layout; also sizes the thumbnail to 16:9. */
  height?: number;
}

/** Clickable YouTube broadcast item linking to the logger stream view for that interval. */
export function YoutubeBroadcastItem({
  broadcast,
  stationSlug,
  layout = 'vertical',
  size = 'large',
  height,
}: YoutubeBroadcastItemProps) {
  const streamStartedAtMs = Date.parse(broadcast.streamStartedAt);
  const streamEndedAtMs = Date.parse(broadcast.streamEndedAt);
  const to = `/logger/s/${stationSlug}?from=${streamStartedAtMs}&to=${streamEndedAtMs}`;
  const isHorizontal = layout === 'horizontal';

  return (
    <ListItem
      disablePadding
      sx={{
        mb: isHorizontal ? 0 : 1,
        ...(isHorizontal && { width: '100%', minWidth: 0 }),
      }}
    >
      <ListItemButton
        component={RouterLink}
        to={to}
        onClick={(event) => event.stopPropagation()}
        title={size === 'xsmall' ? broadcast.video.title : undefined}
        sx={{
          '&:hover': {
            color: 'inherit',
          },
          px: 1,
          py: 1,
          gap: 1,
          alignItems: 'center',
          ...(isHorizontal && {
            pl: 0,
            ...(height != null && { height }),
          }),
        }}
      >
        <Box
          sx={{
            width: 3,
            alignSelf: 'stretch',
            bgcolor: 'primary.main',
            borderRadius: 1,
            flexShrink: 0,
          }}
        />
        {isHorizontal && height != null ? (
          <YoutubeBroadcastThumbnail
            video={broadcast.video}
            sx={{
              borderRadius: 1,
              alignSelf: 'stretch',
              width: 'auto',
              aspectRatio: '16/9',
            }}
          />
        ) : (
          <ListItemIcon sx={{ minWidth: YOUTUBE_BROADCAST_THUMBNAIL_WIDTH, mr: 1.5 }}>
            <YoutubeBroadcastThumbnail
              video={broadcast.video}
              width={YOUTUBE_BROADCAST_THUMBNAIL_WIDTH}
              sx={{ borderRadius: 1 }}
            />
          </ListItemIcon>
        )}
        <ListItemText
          primary={size === 'xsmall' ? undefined : broadcast.video.title}
          secondary={
            <Typography variant="caption" color="text.secondary" component="div" mt={0.75}>
              <LDate
                value={broadcast.streamStartedAt}
                weekday="short"
                day="numeric"
                capitalize
                showTodayYesterday
              />
              {' · '}
              <LTime value={broadcast.streamStartedAt} />
              {size !== 'xsmall' && size !== 'small' && (
                <>
                  {' – '}
                  <LTime value={broadcast.streamEndedAt} />
                  {' · '}
                  <DurationForRange
                    from={broadcast.streamStartedAt}
                    to={broadcast.streamEndedAt}
                    hideSeconds
                  />
                </>
              )}

              <Box
                component={size === 'xsmall' ? 'div' : 'span'}
                sx={{
                  pointerEvents: isHorizontal ? 'none' : undefined,
                  mt: size === 'xsmall' ? 0.5 : undefined,
                  ml: size === 'xsmall' ? undefined : 1,
                }}
              >
                <YoutubeBroadcastPeakViewers peakViewers={broadcast.peakViewers} />
              </Box>
            </Typography>
          }
          slotProps={{
            primary: {
              noWrap: !isHorizontal,
              title: broadcast.video.title,
              sx: {
                mb: 0.5,
                ...(isHorizontal && {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: size === 'xsmall' || size === 'small' ? 1.1 : 1.3,
                  fontSize: size === 'xsmall' || size === 'small' ? '0.9rem' : undefined,
                }),
              },
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}
